/**
 * Blueprint draft mode.
 *
 * This module is deliberately separate from the original recommendation
 * engine. The original engine is ranking/curve based and must remain stable
 * for existing users; Blueprint consumes a versioned forecast snapshot and
 * prices the player in the context of the user's actual roster and draft.
 *
 * Pure module — no fetch, DOM, IndexedDB, or Workers globals.
 */

import type {
  AdpRow,
  DraftState,
  LeagueConfig,
  Player,
  PlayerId,
  Position,
} from './types.ts';
import type { PlayerRankView } from './blend.ts';
import {
  byeConflictPenalty,
  computeRosterNeed,
  computeScarcity,
  replacementBaselines,
  rosterFitPoints,
  type PoolEntry,
  type RosterNeed,
  type ScarcityState,
} from './valuation.ts';
import { POSITION_CURVES } from './data/positionCurves.ts';
import { availabilityAt, buildSurvivorModel, waitingPicks } from './availability.ts';
import { applySpecialistRecommendationPolicy, specialistPolicyFor } from './specialistPolicy.ts';

export const BLUEPRINT_SOURCE_ID = 'blueprint';
export const BLUEPRINT_SCHEMA_VERSION = 1;
export const BLUEPRINT_MAX_TIERS = 9;

const POINTS_PER_STANDARD_DEVIATION = 2.563103;
const MINIMUM_TIER_WIDTH = 5;
const LEADER_WIDTH_FRACTION = 0.03;

/** Base weights from the research blueprint. */
export const BLUEPRINT_BASE_WEIGHTS = {
  marginal_value: 0.4,
  expert_residual: 0.18,
  scarcity: 0.14,
  roster_need: 0.1,
  wait_cost: 0.14,
  upside: 0.03,
  bye_cost: 0.01,
} as const;

export type BlueprintComponent = keyof typeof BLUEPRINT_BASE_WEIGHTS;

/**
 * Runtime forecast contract. The model pipeline may add raw-stat and weekly
 * distribution fields later; these summary fields are the stable minimum the
 * browser needs to explain a recommendation and run the first model version.
 */
export interface BlueprintPlayerForecast {
  player_key: PlayerId;
  position: Position;
  mean_points: number;
  p10_points: number;
  p50_points: number;
  p90_points: number;
  expected_games_active: number;
  breakout_probability: number;
  uncertainty: number;
  bye_week: number | null;
  /** Optional raw-stat forecast parameters for future scoring-aware models. */
  mean_stat_line?: Record<string, number>;
  weekly_distribution?: Record<string, number>;
  correlation_group?: string | null;
  risk_flags?: string[];
}

export interface BlueprintSnapshot {
  schema_version: number;
  model_version: string;
  season: string;
  generated_at: string;
  expires_at: string;
  source: 'open-model' | 'curve-baseline';
  training_seasons: number[];
  data_cutoffs: Record<string, string>;
  attribution: string[];
  players: Record<PlayerId, BlueprintPlayerForecast>;
}

export interface BlueprintScoreComponents {
  marginal_lineup_value: number;
  expert_residual: number;
  scarcity: number;
  roster_need: number;
  wait_cost: number;
  upside: number;
  bye_cost: number;
}

export interface BlueprintScoreContributions extends BlueprintScoreComponents {}

export interface BlueprintValuation {
  player_key: PlayerId;
  projected_points: number;
  p10_points: number;
  p50_points: number;
  p90_points: number;
  expected_games_active: number;
  marginal_lineup_value: number;
  expert_residual: number;
  scarcity_score: number;
  roster_need_score: number;
  wait_cost: number;
  upside_score: number;
  bye_cost: number;
  probability_available_next_pick: number;
  availability_known: boolean;
  expected_next_pick_alternative: number | null;
  expected_regret_if_wait: number;
  final_score: number;
  components: BlueprintScoreComponents;
  contributions: BlueprintScoreContributions;
  risk_flags: string[];
}

export interface BlueprintRecommendation {
  player: Player;
  valuation: BlueprintValuation;
  headline: string;
  reasons: string[];
  comparison: BlueprintRecommendationComparison | null;
}

export interface BlueprintRecommendationComparison {
  compare_to_name: string;
  compare_to_score: number;
  compare_to_lineup_gain: number;
  score_delta: number;
  tied_at_display_precision: boolean;
}

export function blueprintScoresTieAtDisplayPrecision(left: number, right: number): boolean {
  return left.toFixed(3) === right.toFixed(3);
}

/**
 * Classify the complete Blueprint forecast into stable, position-local tiers.
 *
 * A tier is a group of players whose forecast values are close relative to the
 * model's uncertainty. The width is learned from the top of each position's
 * forecast range, with small floors preventing noise from creating a new tier
 * for every player. The complete snapshot is required here: deriving from the
 * current available pool would renumber tiers whenever a player is drafted.
 *
 * Invalid or incomplete forecasts are intentionally omitted. A fallback board
 * estimate may still be used for scoring, but it must not create a confident
 * Blueprint tier claim.
 */
export function deriveBlueprintTiers(
  forecasts: Iterable<BlueprintPlayerForecast>,
  maxTiers = BLUEPRINT_MAX_TIERS,
): Map<PlayerId, number> {
  const grouped = new Map<Position, BlueprintPlayerForecast[]>();

  for (const forecast of forecasts) {
    if (!validTierForecast(forecast)) continue;
    const list = grouped.get(forecast.position) ?? [];
    list.push(forecast);
    grouped.set(forecast.position, list);
  }

  const tiers = new Map<PlayerId, number>();
  const tierLimit = Math.max(1, Math.floor(maxTiers));

  for (const [position, list] of grouped) {
    list.sort((left, right) => {
      const meanDelta = right.mean_points - left.mean_points;
      return meanDelta !== 0 ? meanDelta : left.player_key.localeCompare(right.player_key);
    });

    const depth = POSITION_CURVES[position]?.depth ?? list.length;
    const sample = list.slice(0, Math.max(1, depth));
    const sigmas = sample
      .map((forecast) => (forecast.p90_points - forecast.p10_points) / POINTS_PER_STANDARD_DEVIATION)
      .filter((sigma) => Number.isFinite(sigma) && sigma > 0)
      .sort((left, right) => left - right);
    const medianSigma = median(sigmas);
    const leaderMean = list[0]!.mean_points;
    const tierWidth = Math.max(
      MINIMUM_TIER_WIDTH,
      Math.abs(leaderMean) * LEADER_WIDTH_FRACTION,
      medianSigma,
    );

    const rawTiers = list.map((forecast) =>
      1 + Math.floor(Math.max(0, leaderMean - forecast.mean_points) / tierWidth + Number.EPSILON),
    );
    const distinctRawTiers = [...new Set(rawTiers)].sort((left, right) => left - right);
    const denseRank = new Map(distinctRawTiers.map((rawTier, index) => [rawTier, index + 1]));

    list.forEach((forecast, index) => {
      const rawTier = rawTiers[index]!;
      tiers.set(forecast.player_key, Math.min(tierLimit, denseRank.get(rawTier)!));
    });
  }

  return tiers;
}

function validTierForecast(forecast: BlueprintPlayerForecast): boolean {
  return (
    forecast.player_key.length > 0 &&
    Number.isFinite(forecast.mean_points) &&
    forecast.mean_points > 0 &&
    Number.isFinite(forecast.p10_points) &&
    Number.isFinite(forecast.p90_points) &&
    forecast.p10_points <= forecast.mean_points &&
    forecast.mean_points <= forecast.p90_points
  );
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const middle = Math.floor(values.length / 2);
  return values.length % 2 === 0 ? (values[middle - 1]! + values[middle]!) / 2 : values[middle]!;
}

export interface BlueprintOutput {
  valuations: Map<PlayerId, BlueprintValuation>;
  top: BlueprintRecommendation[];
  order: PlayerId[];
  snapshot: BlueprintSnapshot;
  scarcity: ScarcityState;
  need: RosterNeed;
  tiers: Map<PlayerId, number>;
  my_next_pick: number | null;
  my_pick_after: number | null;
}

export interface BlueprintInput {
  league: LeagueConfig;
  draft: DraftState;
  snapshot: BlueprintSnapshot;
  available: PoolEntry[];
  /** Only independent imported ranking sources should be supplied here. */
  rankView: Map<PlayerId, PlayerRankView> | null;
  adp: Map<PlayerId, AdpRow>;
  playerOf: (id: PlayerId) => Player | undefined;
}

interface RawBlueprint {
  entry: PoolEntry;
  forecast: BlueprintPlayerForecast;
  marginal_value: number;
  expert_residual: number;
  scarcity: number;
  roster_need: number;
  wait_cost: number;
  upside: number;
  bye_cost: number;
  availability_probability: number;
  availability_known: boolean;
  alternative: number | null;
  regret: number;
}

const STAGE_MULTIPLIERS: Record<'early' | 'middle' | 'late', Record<BlueprintComponent, number>> = {
  early: {
    marginal_value: 1.15,
    expert_residual: 1.1,
    scarcity: 0.85,
    roster_need: 0.25,
    wait_cost: 1.15,
    upside: 0.6,
    bye_cost: 0.25,
  },
  middle: {
    marginal_value: 1,
    expert_residual: 0.9,
    scarcity: 1.2,
    roster_need: 0.9,
    wait_cost: 1.15,
    upside: 1,
    bye_cost: 0.5,
  },
  late: {
    marginal_value: 0.75,
    expert_residual: 0.8,
    scarcity: 0.75,
    roster_need: 1.6,
    wait_cost: 0.8,
    upside: 2.5,
    bye_cost: 1,
  },
};

const CORE_POSITIONS: readonly Position[] = ['RB', 'WR', 'TE'];

export function runBlueprint(input: BlueprintInput): BlueprintOutput {
  const { league, draft, snapshot, available, rankView, adp, playerOf } = input;
  const positionOf = (id: PlayerId): Position | null => playerOf(id)?.position ?? null;
  const baselines = replacementBaselines(available, league, 'BEER');
  // Keep the complete snapshot map in the output so a player's tier remains
  // visible and unchanged after he leaves the available pool.
  const tiers = deriveBlueprintTiers(Object.values(snapshot.players));

  const scarcity = computeScarcity(draft.picks, available, new Map(tiers), 12, positionOf);
  const need = computeRosterNeed(draft.my_roster, league, positionOf);
  const specialistPolicy = specialistPolicyFor(draft, league, need);
  const fitOf = rosterFitPoints(draft.my_roster, league, positionOf, baselines);
  const { next, after } = waitingPicks(
    draft.my_draft_slot,
    league.teams,
    league.rounds,
    league.draft_type,
    draft.current_pick_no,
  );

  const survivors = buildSurvivorModel(
    available.map((entry) => ({
      player_key: entry.player.player_id_sleeper,
      position: entry.position,
      points: forecastFor(snapshot, entry.player.player_id_sleeper, entry.position, entry.proj_points).mean_points,
      rank: entry.overall_rank,
    })),
    adp,
    draft,
    league.teams,
    league.rounds,
    league.draft_type,
  );

  const modelPercentiles = positionalPercentiles(available, snapshot);
  const raw: RawBlueprint[] = available.map((entry) => {
    const id = entry.player.player_id_sleeper;
    const forecast = forecastFor(snapshot, id, entry.position, entry.proj_points);
    const position = entry.position;
    const marginal_value =
      forecast.mean_points - (baselines.points[position] ?? 0) + fitOf(position, forecast.mean_points);
    const modelPercentile = modelPercentiles.get(id) ?? 0.5;
    const sourcePercentile = rankView?.get(id)?.blended_percentile;
    const expert_residual = sourcePercentile === undefined ? 0 : sourcePercentile - modelPercentile;
    const fallback = survivors.expectedBest(position);
    const alternative = fallback === undefined ? null : fallback;
    const edgeOverWait = fallback === undefined ? 0 : Math.max(0, forecast.mean_points - fallback);
    const adpRow = adp.get(id);
    const availability_known = next !== null && adpRow !== undefined;
    const availability_probability = availability_known
      ? availabilityAt({
          adp: adpRow!.adp,
          stdev: adpRow!.stdev,
          times_drafted: adpRow!.times_drafted,
          target_pick: next!,
          current_pick: draft.current_pick_no,
        }).probability
      : 0;
    const wait_cost = availability_known ? (1 - availability_probability) * edgeOverWait : 0;
    const upside = clamp01(
      0.6 * forecast.breakout_probability +
        0.4 * Math.max(0, forecast.p90_points - forecast.p50_points) / Math.max(Math.abs(forecast.p50_points), 1),
    );
    const bye_cost = byeConflictPenalty(entry.player, draft.my_roster, league, playerOf);

    return {
      entry,
      forecast,
      marginal_value,
      expert_residual,
      scarcity: scarcity.score[position] ?? 0,
      roster_need: nonlinearRosterNeed(need, position, draft, league),
      wait_cost,
      upside,
      bye_cost,
      availability_probability,
      availability_known,
      alternative,
      regret: availability_known ? Math.max(0, marginal_value - (alternative ?? marginal_value)) * (1 - availability_probability) : 0,
    };
  });

  const weights = effectiveWeights(draft.current_pick_no, league.teams * league.rounds);
  const z = {
    marginal_value: standardize(raw.map((r) => r.marginal_value)),
    expert_residual: standardize(raw.map((r) => r.expert_residual)),
    scarcity: standardize(raw.map((r) => r.scarcity)),
    roster_need: standardize(raw.map((r) => r.roster_need)),
    wait_cost: standardize(raw.map((r) => r.wait_cost)),
    upside: standardize(raw.map((r) => r.upside)),
    bye_cost: standardize(raw.map((r) => r.bye_cost)),
  } satisfies Record<BlueprintComponent, number[]>;

  const valuations = new Map<PlayerId, BlueprintValuation>();
  raw.forEach((r, i) => {
    const contributions: BlueprintScoreContributions = {
      marginal_lineup_value: weights.marginal_value * z.marginal_value[i]!,
      expert_residual: weights.expert_residual * z.expert_residual[i]!,
      scarcity: weights.scarcity * z.scarcity[i]!,
      roster_need: weights.roster_need * z.roster_need[i]!,
      wait_cost: weights.wait_cost * z.wait_cost[i]!,
      upside: weights.upside * z.upside[i]!,
      bye_cost: -weights.bye_cost * z.bye_cost[i]!,
    };
    const final_score = Object.values(contributions).reduce((sum, value) => sum + value, 0);
    const id = r.entry.player.player_id_sleeper;
    valuations.set(id, {
      player_key: id,
      projected_points: r.forecast.mean_points,
      p10_points: r.forecast.p10_points,
      p50_points: r.forecast.p50_points,
      p90_points: r.forecast.p90_points,
      expected_games_active: r.forecast.expected_games_active,
      marginal_lineup_value: r.marginal_value,
      expert_residual: r.expert_residual,
      scarcity_score: r.scarcity,
      roster_need_score: r.roster_need,
      wait_cost: r.wait_cost,
      upside_score: r.upside,
      bye_cost: r.bye_cost,
      probability_available_next_pick: r.availability_probability,
      availability_known: r.availability_known,
      expected_next_pick_alternative: r.alternative,
      expected_regret_if_wait: r.regret,
      final_score,
      components: {
        marginal_lineup_value: r.marginal_value,
        expert_residual: r.expert_residual,
        scarcity: r.scarcity,
        roster_need: r.roster_need,
        wait_cost: r.wait_cost,
        upside: r.upside,
        bye_cost: r.bye_cost,
      },
      contributions,
      risk_flags: r.forecast.risk_flags ?? [],
    });
  });

  const sorted = [...valuations.values()].sort((a, b) => {
    const scoreDelta = b.final_score - a.final_score;
    if (scoreDelta !== 0) return scoreDelta;
    const gainDelta = b.marginal_lineup_value - a.marginal_lineup_value;
    if (gainDelta !== 0) return gainDelta;
    return a.player_key.localeCompare(b.player_key);
  });
  const recommendations = applySpecialistRecommendationPolicy(
    applyOneQbPolicy(sorted, league, draft, positionOf),
    (valuation) => positionOf(valuation.player_key),
    specialistPolicy,
    5,
  );
  const top = recommendations.slice(0, 5).map((valuation, index) => {
    const player = playerOf(valuation.player_key)!;
    const compareTo = index === 0 ? recommendations[1] : recommendations[0];
    const compareToPlayer = compareTo ? playerOf(compareTo.player_key) : undefined;
    const scoreDelta = compareTo ? valuation.final_score - compareTo.final_score : 0;
    return {
      player,
      valuation,
      headline: blueprintHeadline(player, valuation, need, scarcity, next),
      reasons: blueprintReasons(player, valuation, snapshot.model_version),
      comparison:
        compareTo && compareToPlayer
          ? {
              compare_to_name: compareToPlayer.full_name,
              compare_to_score: compareTo.final_score,
              compare_to_lineup_gain: compareTo.marginal_lineup_value,
              score_delta: scoreDelta,
              tied_at_display_precision: blueprintScoresTieAtDisplayPrecision(valuation.final_score, compareTo.final_score),
            }
          : null,
    } satisfies BlueprintRecommendation;
  });

  return {
    valuations,
    top,
    order: sorted.map((v) => v.player_key),
    snapshot,
    scarcity,
    need,
    tiers,
    my_next_pick: next,
    my_pick_after: after,
  };
}

function forecastFor(snapshot: BlueprintSnapshot, id: PlayerId, position: Position, fallback: number): BlueprintPlayerForecast {
  const found = snapshot.players[id];
  if (found) return found;
  return {
    player_key: id,
    position,
    mean_points: fallback,
    p10_points: fallback * 0.75,
    p50_points: fallback,
    p90_points: fallback * 1.25,
    expected_games_active: 17,
    breakout_probability: 0,
    uncertainty: 0.25,
    bye_week: null,
    risk_flags: ['Forecast unavailable; using the current board estimate.'],
  };
}

function positionalPercentiles(available: PoolEntry[], snapshot: BlueprintSnapshot): Map<PlayerId, number> {
  const grouped = new Map<Position, { id: PlayerId; points: number }[]>();
  for (const entry of available) {
    const list = grouped.get(entry.position) ?? [];
    list.push({ id: entry.player.player_id_sleeper, points: forecastFor(snapshot, entry.player.player_id_sleeper, entry.position, entry.proj_points).mean_points });
    grouped.set(entry.position, list);
  }
  const out = new Map<PlayerId, number>();
  for (const list of grouped.values()) {
    list.sort((a, b) => b.points - a.points);
    const denominator = Math.max(list.length - 1, 1);
    list.forEach((item, i) => out.set(item.id, 1 - i / denominator));
  }
  return out;
}

function nonlinearRosterNeed(need: RosterNeed, position: Position, draft: DraftState, league: LeagueConfig): number {
  const mandatoryOpen = Object.values(need.open_starters).reduce((sum, value) => sum + value, 0);
  const picksRemaining = Math.max(1, league.rounds * league.teams - draft.current_pick_no + 1);
  const riskRamp = clamp01((mandatoryOpen + 3 - picksRemaining) / 3);
  return (need.score[position] ?? 0) * (0.15 + 0.85 * riskRamp);
}

function effectiveWeights(currentPick: number, totalPicks: number): Record<BlueprintComponent, number> {
  const progress = clamp01((currentPick - 1) / Math.max(totalPicks - 1, 1));
  const stage = progress < 1 / 3 ? 'early' : progress < 2 / 3 ? 'middle' : 'late';
  const multipliers = STAGE_MULTIPLIERS[stage];
  const raw = {} as Record<BlueprintComponent, number>;
  let total = 0;
  for (const key of Object.keys(BLUEPRINT_BASE_WEIGHTS) as BlueprintComponent[]) {
    raw[key] = BLUEPRINT_BASE_WEIGHTS[key] * multipliers[key];
    total += raw[key];
  }
  return Object.fromEntries(
    (Object.keys(raw) as BlueprintComponent[]).map((key) => [key, raw[key]! / total]),
  ) as Record<BlueprintComponent, number>;
}

function applyOneQbPolicy(
  sorted: BlueprintValuation[],
  league: LeagueConfig,
  draft: DraftState,
  positionOf: (id: PlayerId) => Position | null,
): BlueprintValuation[] {
  if (league.league_type !== 'redraft' || league.starters_by_position.QB !== 1 || league.superflex) return sorted;
  if (!draft.my_roster.some((id) => positionOf(id) === 'QB')) return sorted;
  const openCore = CORE_POSITIONS.some((position) => {
    const required = league.starters_by_position[position] ?? 0;
    const owned = draft.my_roster.filter((id) => positionOf(id) === position).length;
    return owned < required;
  });
  if (!openCore) return sorted;
  return [
    ...sorted.filter((v) => positionOf(v.player_key) !== 'QB'),
    ...sorted.filter((v) => positionOf(v.player_key) === 'QB'),
  ];
}

function blueprintHeadline(
  player: Player,
  valuation: BlueprintValuation,
  need: RosterNeed,
  scarcity: ScarcityState,
  nextPick: number | null,
): string {
  const position = player.position ?? 'FLEX';
  if (valuation.availability_known && valuation.probability_available_next_pick < 0.25 && nextPick !== null) {
    return `${player.full_name} is unlikely to survive to pick ${nextPick}.`;
  }
  if (valuation.roster_need_score > 0.7 && player.position && (need.open_starters[player.position] ?? 0) > 0) {
    return `${player.full_name} adds projected lineup value at your open ${position} slot (${signedPoints(valuation.marginal_lineup_value)} over replacement).`;
  }
  if (valuation.upside_score > 0.65) {
    return `${player.full_name} combines a realistic starter path with meaningful upside.`;
  }
  if (player.position && (scarcity.tier_remaining[player.position] ?? 99) <= 2) {
    return `${player.full_name} is near the edge of a ${position} tier cliff.`;
  }
  return `${player.full_name} adds ${signedPoints(valuation.marginal_lineup_value)} projected lineup points over the contextual replacement baseline.`;
}

function signedPoints(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}`;
}

function blueprintReasons(player: Player, valuation: BlueprintValuation, modelVersion: string): string[] {
  const reasons = [
    `Projected range: ${valuation.p10_points.toFixed(0)}–${valuation.p90_points.toFixed(0)} points, with ${valuation.expected_games_active.toFixed(1)} expected games active.`,
    `Marginal lineup value: ${valuation.marginal_lineup_value >= 0 ? '+' : ''}${valuation.marginal_lineup_value.toFixed(1)} points over the contextual replacement baseline.`,
    `${Math.round(valuation.upside_score * 100)}% upside signal from breakout probability and upper-tail outcomes.`,
  ];
  if (valuation.availability_known) {
    reasons.push(`${Math.round(valuation.probability_available_next_pick * 100)}% chance to remain available at your next pick; expected regret from waiting is ${valuation.expected_regret_if_wait.toFixed(1)} points.`);
  } else {
    reasons.push('Next-pick availability is unknown because no ADP row is available for this player.');
  }
  if (valuation.risk_flags.length) reasons.push(`Risk: ${valuation.risk_flags.join(' ')}`);
  reasons.push(`Blueprint model ${modelVersion}.`);
  return reasons;
}

function standardize(values: number[]): number[] {
  if (values.length === 0) return [];
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  const deviation = Math.sqrt(variance);
  if (deviation === 0) return values.map(() => 0);
  return values.map((value) => clamp(value === mean ? 0 : (value - mean) / deviation, -3, 3));
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
const clamp01 = (value: number): number => clamp(value, 0, 1);
